import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaidIcon from '@mui/icons-material/Paid';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import {
  closeCashSession,
  createCashMovement,
  getCashSession,
  getCashSessions,
  updateCashMovement,
} from '../services/api';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../utils/reportBranding';
import { getAuthPermissoes } from '../services/authStorage';
import { toDateInputValue } from '../utils/datetimeLocal';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const fmtDate = (value) =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';

const isSessionClosed = (session) =>
  Boolean(session?.fechada || session?.dataFechamento);

const localDate = toDateInputValue;

const initialFilters = (params) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const status = params?.get('status') || '';
  return {
    de: params?.get('de') || localDate(first),
    ate: params?.get('ate') || localDate(now),
    status: status === 'aberta' || status === 'fechada' ? status : '',
  };
};

function SummaryCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={900} color={color}>{value}</Typography>
    </Paper>
  );
}

/// PDF no mesmo padrão visual dos relatórios do Gestão (HTML → window.print).
function printCashSessionPdf(popup, detail, company = {}) {
  const status = isSessionClosed(detail) ? 'Fechada' : 'Aberta';
  const vendasConvenio = (detail.formasPagamento || [])
    .filter((f) => /conv[eê]nio/i.test(String(f.descricao || '')))
    .reduce((s, f) => s + Number(f.vendas || 0), 0);

  const cards = [
    ['Vendas', currency(detail.totalVendas)],
    ['Qtd. vendas', String(detail.quantidadeVendas ?? 0)],
    ['Abertura', currency(detail.abertura)],
    ['Suprimentos', currency(detail.suprimentos)],
    ['Sangrias', currency(detail.sangrias)],
    ['Vendas em convênio', currency(vendasConvenio)],
    ['Recebimentos convênio', currency(detail.recebimentosConvenio)],
    ['Total líquido', currency(detail.totalLiquidoCaixa)],
  ]
    .map(
      ([label, value]) =>
        `<div><span>${html(label)}</span><strong>${html(value)}</strong></div>`
    )
    .join('');

  const formasRows = (detail.formasPagamento || [])
    .map(
      (f) => `<tr>
        <td>${html(f.descricao)}</td>
        <td class="right">${html(currency(f.vendas))}</td>
        <td class="right">${html(currency(f.recebimentosConvenio))}</td>
        <td class="right"><strong>${html(currency(f.valorSistema))}</strong></td>
      </tr>`
    )
    .join('');

  const movRows = (detail.movimentos || [])
    .map(
      (m) => `<tr>
        <td>${html(fmtDate(m.data))}</td>
        <td>${html(m.tipoDescricao)}</td>
        <td>${html(m.motivo || '-')}</td>
        <td>${html(m.usuario)}</td>
        <td class="right">${html(currency(m.valor))}</td>
      </tr>`
    )
    .join('');

  const confRows = (detail.conferencias || [])
    .map(
      (c) => `<tr>
        <td>${html(c.descricao)}</td>
        <td class="right">${html(currency(c.valorSistema))}</td>
        <td class="right">${html(currency(c.valorInformado))}</td>
        <td class="right ${Number(c.diferenca) === 0 ? 'ok' : 'diff'}">${html(currency(c.diferenca))}</td>
      </tr>`
    )
    .join('');

  popup.document.open();
  popup.document.write(`<!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Sessão de caixa #${html(detail.id)}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          * { box-sizing: border-box; }
          body { margin: 0; color: #172033; font: 10px Arial, sans-serif; }
          header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #0495f0; padding-bottom: 8px; gap: 16px; }
          h1 { margin: 2px 0 0; font-size: 20px; }
          h2 { margin: 16px 0 8px; font-size: 13px; color: #172033; }
          ${REPORT_BRAND_CSS}
          .meta { color: #5f6878; text-align: right; line-height: 1.45; }
          .chip { display: inline-block; margin-top: 6px; padding: 2px 8px; border-radius: 999px; font-weight: 700; font-size: 10px;
            background: ${isSessionClosed(detail) ? '#eef1f6' : '#e7f8ee'};
            color: ${isSessionClosed(detail) ? '#5f6878' : '#1b7a3d'}; }
          .summary { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
          .summary div { min-width: 110px; flex: 1; border: 1px solid #d8dde7; border-radius: 5px; padding: 7px 9px; }
          .summary span, .summary strong { display: block; }
          .summary span { color: #687386; font-size: 9px; }
          .summary strong { margin-top: 2px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          th { background: #eaf6fe; color: #172033; text-align: left; padding: 6px; border-bottom: 1px solid #9fcfec; }
          td { padding: 5px 6px; border-bottom: 1px solid #e2e6ed; vertical-align: top; }
          td.right, th.right { text-align: right; }
          td.ok { color: #1b7a3d; font-weight: 700; }
          td.diff { color: #c62828; font-weight: 700; }
          .empty { color: #687386; margin: 4px 0 12px; }
          footer { position: fixed; bottom: -7mm; right: 0; color: #737c8c; font-size: 8px; }
          @media screen { body { padding: 18px; } }
        </style>
      </head>
      <body>
        <header>
          ${reportBrandHtml(company, {
            title: `Sessão de caixa #${detail.id}`,
            fallbackName: detail.estabelecimento || 'Estabelecimento',
            extraHtml: `<span class="chip">${html(status)}</span>`,
          })}
          <div class="meta">
            PDV: ${html(detail.pdv)} · Turno ${html(detail.turno)}<br>
            Operador: ${html(detail.usuario)}<br>
            Abertura: ${html(fmtDate(detail.dataAbertura))}<br>
            Fechamento: ${html(fmtDate(detail.dataFechamento))}<br>
            Emitido em ${html(new Date().toLocaleString('pt-BR'))}
          </div>
        </header>
        <section class="summary">${cards}</section>

        <h2>Formas de pagamento</h2>
        ${(detail.formasPagamento || []).length
          ? `<table>
              <thead><tr>
                <th>Forma</th><th class="right">Vendas</th>
                <th class="right">Recebido</th><th class="right">Sistema</th>
              </tr></thead>
              <tbody>${formasRows}</tbody>
            </table>`
          : '<p class="empty">Nenhuma forma de pagamento.</p>'}

        <h2>Movimentos de caixa</h2>
        ${(detail.movimentos || []).length
          ? `<table>
              <thead><tr>
                <th>Data</th><th>Tipo</th><th>Motivo</th>
                <th>Operador</th><th class="right">Valor</th>
              </tr></thead>
              <tbody>${movRows}</tbody>
            </table>`
          : '<p class="empty">Nenhum movimento.</p>'}

        ${(detail.conferencias || []).length
          ? `<h2>Conferência</h2>
            <table>
              <thead><tr>
                <th>Forma</th><th class="right">Sistema</th>
                <th class="right">Informado</th><th class="right">Diferença</th>
              </tr></thead>
              <tbody>${confRows}</tbody>
            </table>`
          : ''}

        <footer>ZenGestão · Sessão #${html(detail.id)}</footer>
      </body>
    </html>`);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 300);
}

export default function CashSessionList() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const can = useCallback(
    (key) => permissions == null || Boolean(permissions[key]),
    [permissions]
  );
  const [searchParams] = useSearchParams();
  const fromUrl = useMemo(() => initialFilters(searchParams), [searchParams]);
  const [filters, setFilters] = useState(fromUrl);
  const [applied, setApplied] = useState(fromUrl);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [conferenceOpen, setConferenceOpen] = useState(false);
  const [conference, setConference] = useState({});
  const [closing, setClosing] = useState(false);
  const [actionAnchor, setActionAnchor] = useState(null);
  const [actionSession, setActionSession] = useState(null);
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementSession, setMovementSession] = useState(null);
  const [movementEditing, setMovementEditing] = useState(null);
  const [movementForm, setMovementForm] = useState({
    tipo: 2,
    valor: '',
    motivo: '',
  });
  const [movementSaving, setMovementSaving] = useState(false);

  useEffect(() => {
    const next = initialFilters(searchParams);
    setFilters(next);
    setApplied(next);
    setPage(1);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCashSessions({
        ...applied,
        estabelecimentoId,
        indice: page,
        qtde: 20,
      });
      setData(response.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível carregar as sessões.');
    } finally {
      setLoading(false);
    }
  }, [applied, page, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const sessionAccessors = useMemo(() => ({
    sessao: (s) => s.id,
    aberturaFechamento: (s) => s.dataAbertura,
    pdvOperador: (s) => `${s.pdv || ''} ${s.usuario || ''}`,
    vendas: (s) => Number(s.totalVendas) || 0,
    sangrias: (s) => Number(s.totalSangrias) || 0,
    status: (s) => (isSessionClosed(s) ? 1 : 0),
  }), []);
  const { sorted: sessionsOrdenadas, orderBy, order, requestSort } = useClientSort(
    data.items || [],
    'id',
    'desc',
    sessionAccessors,
  );

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    setError('');
    try {
      const response = await getCashSession(id);
      setDetail(response.data);
      return response.data;
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao carregar o resumo da sessão.');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    const loaded = await fetchDetail(id);
    if (!loaded) setDetailOpen(false);
  };

  const openConference = async (sessionId = detail?.id) => {
    if (!sessionId) return;
    const current = detail?.id === sessionId ? detail : await fetchDetail(sessionId);
    if (!current) return;
    if (isSessionClosed(current)) {
      setError('Esta sessão já está fechada.');
      return;
    }

    const values = {};
    (current.formasPagamento || []).forEach((forma) => {
      const saved = (current.conferencias || []).find(
        (x) => x.formaPagamentoId === forma.formaPagamentoId
      );
      values[forma.formaPagamentoId] = String(saved?.valorInformado ?? forma.valorSistema);
    });
    setConference(values);
    setConferenceOpen(true);
  };

  const openActions = (event, session) => {
    // Define a sessão antes do âncora para o Menu nunca renderizar
    // opções de sessão aberta com actionSession === null.
    setActionSession(session);
    setActionAnchor(event.currentTarget);
  };

  const closeActions = () => {
    setActionAnchor(null);
    // Mantém actionSession até a próxima abertura — evita flash de
    // Movimentar/Encerrar quando o Menu fecha com sessão nula.
  };

  const sessionIsOpen = Boolean(actionSession) && !isSessionClosed(actionSession);

  const printSession = async (sessionId) => {
    if (!sessionId) return;
    const popup = window.open('', '_blank', 'width=900,height=1100');
    if (!popup) {
      setError('O navegador bloqueou a impressão. Libere pop-ups para este site.');
      return;
    }
    popup.document.write(
      '<!doctype html><title>Gerando PDF</title><p style="font:16px Arial;padding:24px">Preparando sessão de caixa…</p>'
    );

    try {
      const [sessionResp, company] = await Promise.all([
        detail?.id === sessionId
          ? Promise.resolve({ data: detail })
          : getCashSession(sessionId),
        loadReportCompany(),
      ]);
      const loaded = sessionResp.data;
      if (!loaded) throw new Error('Sessão não encontrada.');
      printCashSessionPdf(popup, loaded, company);
    } catch (e) {
      popup.close();
      setError(e?.response?.data?.error || e?.message || 'Não foi possível gerar o PDF.');
    }
  };

  const openMovement = (session, movement = null) => {
    if (isSessionClosed(session)) {
      setError('Não é possível movimentar uma sessão fechada.');
      return;
    }
    setMovementSession(session);
    setMovementEditing(movement);
    setMovementForm({
      tipo: movement?.tipo ?? 2,
      valor: movement ? String(movement.valor).replace('.', ',') : '',
      motivo: movement?.motivo ?? '',
    });
    setMovementOpen(true);
  };

  const saveMovement = async () => {
    const valor = Number(String(movementForm.valor).replace(',', '.'));
    if (!(valor > 0)) {
      setError('Informe um valor maior que zero.');
      return;
    }

    setMovementSaving(true);
    setError('');
    const payload = {
      tipo: Number(movementForm.tipo),
      valor,
      motivo: movementForm.motivo.trim(),
    };
    try {
      if (movementEditing) {
        await updateCashMovement(movementSession.id, movementEditing.id, payload);
      } else {
        await createCashMovement(movementSession.id, payload);
      }
      setMovementOpen(false);
      await load();
      if (detailOpen && detail?.id === movementSession.id) {
        await fetchDetail(movementSession.id);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível salvar o movimento.');
    } finally {
      setMovementSaving(false);
    }
  };

  const finish = async () => {
    setClosing(true);
    setError('');
    try {
      const conferencias = (detail.formasPagamento || []).map((forma) => ({
        formaPagamentoId: forma.formaPagamentoId,
        valorInformado: Number(
          String(conference[forma.formaPagamentoId] || 0).replace(',', '.')
        ),
      }));
      await closeCashSession(detail.id, conferencias);
      setConferenceOpen(false);
      const refreshed = await getCashSession(detail.id);
      setDetail(refreshed.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível fechar a sessão.');
    } finally {
      setClosing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Sessões de caixa"
        subtitle="Aberturas, vendas, movimentações, convênio e conferência."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.de}
            onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.ate}
            onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="aberta">Abertas</MenuItem>
            <MenuItem value="fechada">Fechadas</MenuItem>
          </TextField>
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => {
              setPage(1);
              // Cópia nova a cada clique: sem trocar a referência o React
              // descarta o setState e a lista não recarrega.
              setApplied({ ...filters });
            }}
          >
            Filtrar
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="sessao" label="Sessão" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="aberturaFechamento" label="Abertura / fechamento" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="pdvOperador" label="PDV / operador" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="vendas" label="Vendas" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="sangrias" label="Sangrias" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessionsOrdenadas.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell data-label="Sessão">
                  <Typography fontWeight={800}>#{session.id}</Typography>
                  <Typography variant="caption">{session.estabelecimento}</Typography>
                </TableCell>
                <TableCell data-label="Abertura / fechamento">
                  <Typography variant="body2">{fmtDate(session.dataAbertura)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session.dataFechamento ? fmtDate(session.dataFechamento) : 'Em andamento'}
                  </Typography>
                </TableCell>
                <TableCell data-label="PDV / operador">
                  <Typography variant="body2">{session.pdv}</Typography>
                  <Typography variant="caption">{session.usuario}</Typography>
                </TableCell>
                <TableCell align="right" data-label="Vendas">
                  <Typography fontWeight={700}>{currency(session.totalVendas)}</Typography>
                  <Typography variant="caption">{session.quantidadeVendas} vendas</Typography>
                </TableCell>
                <TableCell align="right" data-label="Sangrias">{currency(session.totalSangrias)}</TableCell>
                <TableCell data-label="Status">
                  <Chip
                    size="small"
                    color={isSessionClosed(session) ? 'default' : 'success'}
                    label={isSessionClosed(session) ? 'Fechada' : 'Aberta'}
                  />
                </TableCell>
                <TableCell align="right" data-actions="">
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<MoreVertIcon />}
                    aria-label={`Ações da sessão ${session.id}`}
                    onClick={(event) => openActions(event, session)}
                  >
                    Ações
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !(data.items || []).length && (
              <TableRow>
                <TableCell colSpan={7} align="center">Nenhuma sessão encontrada.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={closeActions}
      >
        <MenuItem
          onClick={() => {
            const id = actionSession?.id;
            closeActions();
            if (id) openDetail(id);
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} />
          Ver resumo
        </MenuItem>
        <MenuItem
          onClick={() => {
            const id = actionSession?.id;
            closeActions();
            if (id) printSession(id);
          }}
        >
          <PrintIcon fontSize="small" sx={{ mr: 1.5 }} />
          Imprimir PDF
        </MenuItem>
        {sessionIsOpen && can('movimentaCaixa') && (
          <MenuItem
            onClick={() => {
              const session = actionSession;
              closeActions();
              if (session) openMovement(session);
            }}
          >
            <PaidIcon fontSize="small" sx={{ mr: 1.5 }} />
            Movimentar caixa
          </MenuItem>
        )}
        {sessionIsOpen && can('fechaCaixa') && (
          <MenuItem
            onClick={() => {
              const id = actionSession?.id;
              closeActions();
              if (id) openConference(id);
            }}
          >
            <LockIcon fontSize="small" sx={{ mr: 1.5 }} />
            Conferir e encerrar
          </MenuItem>
        )}
      </Menu>

      {data.totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Resumo da sessão {detail ? `#${detail.id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <LinearProgress />}
          {detail && (
            <Stack spacing={3}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  color={isSessionClosed(detail) ? 'default' : 'success'}
                  label={isSessionClosed(detail) ? 'Fechada' : 'Aberta'}
                />
                <Typography>{detail.estabelecimento}</Typography>
                <Typography color="text.secondary">•</Typography>
                <Typography>{detail.pdv}</Typography>
                <Typography color="text.secondary">•</Typography>
                <Typography>{detail.usuario}</Typography>
              </Stack>

              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}><SummaryCard label="Vendas" value={currency(detail.totalVendas)} /></Grid>
                <Grid item xs={6} md={3}><SummaryCard label="Recebimentos convênio" value={currency(detail.recebimentosConvenio)} /></Grid>
                <Grid item xs={6} md={3}><SummaryCard label="Suprimentos" value={currency(detail.suprimentos)} color="success.main" /></Grid>
                <Grid item xs={6} md={3}><SummaryCard label="Sangrias" value={currency(detail.sangrias)} color="error.main" /></Grid>
                <Grid item xs={12} md={3}><SummaryCard label="Total líquido" value={currency(detail.totalLiquidoCaixa)} color="primary.main" /></Grid>
              </Grid>

              <Box>
                <Typography variant="h6" fontWeight={800}>Formas de pagamento</Typography>
                <ResponsiveTable>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Forma</TableCell>
                        <TableCell align="right">Vendas</TableCell>
                        <TableCell align="right">Convênio recebido</TableCell>
                        <TableCell align="right">Sistema</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detail.formasPagamento || []).map((forma) => (
                        <TableRow key={forma.formaPagamentoId}>
                          <TableCell data-label="Forma">{forma.descricao}</TableCell>
                          <TableCell align="right" data-label="Vendas">{currency(forma.vendas)}</TableCell>
                          <TableCell align="right" data-label="Convênio recebido">{currency(forma.recebimentosConvenio)}</TableCell>
                          <TableCell align="right" data-label="Sistema"><strong>{currency(forma.valorSistema)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTable>
              </Box>

              <Divider />
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={800}>Movimentos de caixa</Typography>
                  {!isSessionClosed(detail) && can('movimentaCaixa') && (
                    <Button
                      size="small"
                      startIcon={<PaidIcon />}
                      onClick={() => openMovement(detail)}
                    >
                      Novo movimento
                    </Button>
                  )}
                </Stack>
                {(detail.movimentos || []).length === 0 ? (
                  <Typography color="text.secondary">Nenhum movimento.</Typography>
                ) : (
                  <ResponsiveTable>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Motivo</TableCell>
                          <TableCell>Operador</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.movimentos.map((movement) => (
                          <TableRow key={movement.id}>
                            <TableCell data-label="Data">{fmtDate(movement.data)}</TableCell>
                            <TableCell data-label="Tipo">{movement.tipoDescricao}</TableCell>
                            <TableCell data-label="Motivo">{movement.motivo || '-'}</TableCell>
                            <TableCell data-label="Operador">{movement.usuario}</TableCell>
                            <TableCell align="right" data-label="Valor">{currency(movement.valor)}</TableCell>
                            <TableCell align="center" data-actions="">
                              {!isSessionClosed(detail) &&
                                movement.tipo !== 1 &&
                                can('editaMovimentoCaixa') && (
                                <IconButton
                                  size="small"
                                  aria-label={`Editar movimento ${movement.id}`}
                                  onClick={() => openMovement(detail, movement)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ResponsiveTable>
                )}
              </Box>

              {!!detail.conferencias?.length && (
                <Box>
                  <Typography variant="h6" fontWeight={800}>Conferência</Typography>
                  <ResponsiveTable>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Forma</TableCell>
                          <TableCell align="right">Sistema</TableCell>
                          <TableCell align="right">Informado</TableCell>
                          <TableCell align="right">Diferença</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.conferencias.map((row) => (
                          <TableRow key={row.formaPagamentoId}>
                            <TableCell data-label="Forma">{row.descricao}</TableCell>
                            <TableCell align="right" data-label="Sistema">{currency(row.valorSistema)}</TableCell>
                            <TableCell align="right" data-label="Informado">{currency(row.valorInformado)}</TableCell>
                            <TableCell
                              align="right"
                              data-label="Diferença"
                              sx={{ color: row.diferenca === 0 ? 'success.main' : 'error.main' }}
                            >
                              {currency(row.diferenca)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ResponsiveTable>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail && (
            <Button
              startIcon={<PrintIcon />}
              onClick={() => printSession(detail.id)}
            >
              Imprimir PDF
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={movementOpen}
        onClose={() => !movementSaving && setMovementOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {movementEditing ? 'Editar movimento' : 'Movimentar caixa'}
          {movementSession ? ` · sessão #${movementSession.id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              select
              label="Tipo"
              value={movementForm.tipo}
              onChange={(e) =>
                setMovementForm((p) => ({ ...p, tipo: Number(e.target.value) }))
              }
              fullWidth
            >
              <MenuItem value={2}>Sangria (retirada)</MenuItem>
              <MenuItem value={3}>Suprimento (entrada)</MenuItem>
            </TextField>
            <TextField
              label="Valor"
              value={movementForm.valor}
              onChange={(e) =>
                setMovementForm((p) => ({ ...p, valor: e.target.value }))
              }
              inputMode="decimal"
              placeholder="0,00"
              autoFocus
              fullWidth
            />
            <TextField
              label="Motivo (opcional)"
              value={movementForm.motivo}
              onChange={(e) =>
                setMovementForm((p) => ({ ...p, motivo: e.target.value }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            {movementEditing && (
              <Alert severity="info">
                A data e o operador originais do movimento serão preservados.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovementOpen(false)} disabled={movementSaving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveMovement} disabled={movementSaving}>
            {movementSaving ? 'Salvando...' : 'Salvar movimento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conferenceOpen} onClose={() => setConferenceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Conferir e encerrar sessão #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            O fechamento é definitivo. Informe o valor efetivamente contado em cada forma.
          </Alert>
          <Stack spacing={2}>
            {(detail?.formasPagamento || []).map((forma) => {
              const informed = Number(
                String(conference[forma.formaPagamentoId] || 0).replace(',', '.')
              );
              const difference = informed - Number(forma.valorSistema);
              return (
                <TextField
                  key={forma.formaPagamentoId}
                  label={`${forma.descricao} · sistema ${currency(forma.valorSistema)}`}
                  value={conference[forma.formaPagamentoId] ?? ''}
                  onChange={(e) =>
                    setConference((p) => ({
                      ...p,
                      [forma.formaPagamentoId]: e.target.value,
                    }))
                  }
                  helperText={`Diferença: ${currency(difference)}`}
                  error={Math.abs(difference) > 0.009}
                  fullWidth
                />
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConferenceOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" disabled={closing} onClick={finish}>
            Confirmar fechamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
